import { Flex, type FlexProps } from '@commons/containers/Flex';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalBlock, DrupalSection } from '@commons/types/drupal';
import type { Alignment, Spacing } from '@commons/types/styles';

/**
 * SectionFooterRegion component.
 */

const SectionFooterRegion = (props: FlexProps) => {
  const { children, align = 'start', ref, ...rest } = props;

  return (
    <Flex ref={ref} align={align} direction="column" {...rest}>
      {children}
    </Flex>
  );
};

/**
 * Type definition of a Drupal section footer.
 */

export type DrupalSectionFooter = {
  marginTop: Spacing;
  horizontalAlignment: Alignment;
};

/**
 * Function to render the footer region of a section.
 *
 * @param blocks - The blocks to render.
 * @param footer - The footer properties.
 * @param section - The section properties (used by Craft.js).
 * @param additionalProps - Additional properties to pass to the region component.
 *
 * @returns The rendered footer region.
 */

export const renderFooterRegion = (
  blocks: DrupalBlock[],
  footer: DrupalSectionFooter,
  section: Partial<DrupalSection>,
  additionalProps?: FlexProps
) => {
  const { horizontalAlignment, marginTop } = footer;

  const renderedFooterBlocks = blocks.map(PageBuilder.renderBlock);

  const props = {
    children: renderedFooterBlocks,
    align: horizontalAlignment,
    marginTop,
    ...additionalProps,
  };

  return PageBuilder.renderRegion('footer', SectionFooterRegion, props, section);
};
